import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { AppShell } from '@/app/AppShell';
import { LocalizationProvider, useStrings } from '@/i18n/Localization';
import { ChannelInfoScreen } from '@/screens/ChannelInfoScreen';
import { ConversationScreen, NoConversationSelected } from '@/screens/ConversationScreen';
import { GroupInfoScreen } from '@/screens/GroupInfoScreen';
import { SettingsScreen } from '@/screens/SettingsScreen';
import { SignInScreen } from '@/screens/SignInScreen';
import { ConnectionStatusProvider } from '@/transport/ConnectionStatusProvider';
import { SessionProvider, useSession } from '@/transport/SessionProvider';

/**
 * Application root.
 *
 * A client-rendered single-page application: Section 11 excludes Next.js,
 * server-side rendering, server components and GraphQL, so routing is a browser
 * router over the local store and there is no data loader that talks to a
 * server before a screen renders.
 */
function Routed() {
  const { account, restoring } = useSession();
  const { t, direction } = useStrings();

  if (restoring) {
    // A determinate wait rather than a blank page: the session restore is a
    // network round trip and the reader should know something is happening.
    return (
      <p role="status" style={{ padding: '24px' }}>
        {t('app.restoring')}
      </p>
    );
  }

  if (!account) {
    return <SignInScreen />;
  }

  return (
    <BrowserRouter>
      <div dir={direction} style={{ height: '100%' }}>
        <Routes>
          <Route path="/" element={<AppShell />}>
            <Route index element={<NoConversationSelected />} />
            <Route path="c/:conversationId" element={<ConversationScreen />} />
            {/*
              Group and channel information are routes rather than dialogs so
              that back navigation returns to the conversation with its scroll
              position intact, which is what Section 14 requires of the
              single-column layout below 900 px.
            */}
            <Route path="c/:conversationId/group" element={<GroupInfoScreen />} />
            <Route path="c/:conversationId/channel" element={<ChannelInfoScreen />} />
            <Route path="settings" element={<SettingsScreen />} />
          </Route>
          {/* An unknown path returns to the inbox rather than a dead end. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export function App() {
  return (
    <LocalizationProvider>
      <SessionProvider>
        <ConnectionStatusProvider>
          <Routed />
        </ConnectionStatusProvider>
      </SessionProvider>
    </LocalizationProvider>
  );
}
